import { useTranslation } from "react-i18next";
import { useDeleteZoneEvent } from "@/api/hooks/zone-events/use-delete-zone-event";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import { useLocale } from "@/hooks/use-locale";
import { useSnackbar } from "@/hooks/use-snackbar/use-snackbar";
import { NAMESPACES } from "@/i18n/constants";
import { useOnDemandIrrigationEventTracker } from "@/services/analytics/irrigation/use-on-demand-irrigation-event-tracker";
import { formatTimeAsHoursMinutesAndSeconds } from "@/utils/time";
import { FormContainer } from "./form-container";
import { ResponseTimeHint } from "./response-time-hint";

const TIME_FORMAT_OPTIONS: Intl.DateTimeFormatOptions = {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
};
const DATE_FORMAT_OPTIONS: Intl.DateTimeFormatOptions = {
    month: "short",
    day: "2-digit",
};
interface DeleteEventFormProps {
    onClose: () => void;
    eventId: string;
    zoneId: string;
    eventEndTime: Date;
    eventStartTime: Date;
    responseTimeMs?: number;
}
export function DeleteEventForm({
    onClose,
    eventId,
    zoneId,
    eventEndTime,
    eventStartTime,
    responseTimeMs,
}: DeleteEventFormProps) {
    const locale = useLocale();
    const onDemandIrrigationEventTracker = useOnDemandIrrigationEventTracker(zoneId);
    const { deleteZoneEvent, loading } = useDeleteZoneEvent();
    const { enqueueErrorSnackbar, enqueueSuccessSnackbar } = useSnackbar();
    const { t } = useTranslation(NAMESPACES.FEATURES.CARDS, {
        keyPrefix: "zone.on-demand-irrigation.delete-event-form",
    });

    const onClickDeleteEvent = async () => {
        const response = await deleteZoneEvent({
            eventId: eventId,
        });
        if (response) {
            const durationMs = eventEndTime.getTime() - eventStartTime.getTime();
            onDemandIrrigationEventTracker.trackDelete({ eventId, durationMs });
            enqueueSuccessSnackbar(t("snackbar.success"));
            onClose();
        } else {
            enqueueErrorSnackbar(t("snackbar.error"));
        }
    };

    const startTimeLabel = eventStartTime.toLocaleTimeString(locale, TIME_FORMAT_OPTIONS);
    const endTimeLabel = eventEndTime.toLocaleTimeString(locale, TIME_FORMAT_OPTIONS);
    const dateLabel = eventStartTime.toLocaleDateString(locale, DATE_FORMAT_OPTIONS);
    const duration = eventEndTime.getTime() - eventStartTime.getTime();
    return (
        <FormContainer title={t("title")} onClose={onClose} variant={"delete"}>
            <div className={"text-muted-foreground flex flex-col items-center justify-between w-full gap-2 text-sm"}>
                <span>{t("description")}</span>
                <p className={"rounded-lg"}>
                    {dateLabel} • {startTimeLabel} - {endTimeLabel} (
                    {formatTimeAsHoursMinutesAndSeconds(duration, { includeSecondsWithMinutes: false })})
                </p>
            </div>
            <div className={"flex flex-col items-center jusity-center gap-1"}>
                <Button
                    className={"w-full p-3"}
                    variant={"destructive"}
                    onClick={onClickDeleteEvent}
                    disabled={loading}
                >
                    {loading ? <Spinner className={"size-4"} /> : t("buttons.delete")}
                </Button>
                <ResponseTimeHint responseTimeMs={responseTimeMs} className={"mt-0"} />
            </div>
        </FormContainer>
    );
}
